export const localeSpecs = [
  { code: "en", direction: "ltr", label: "English", nativeLabel: "English" },
  { code: "fr", direction: "ltr", label: "French", nativeLabel: "Français" },
  { code: "ar", direction: "rtl", label: "Arabic", nativeLabel: "العربية" },
] as const;

export type Locale = (typeof localeSpecs)[number]["code"];

export const defaultLocale: Locale = "en";
export const translatedLocales = ["fr", "ar"] as const satisfies readonly Locale[];

export function translatedLocale(value: string): (typeof translatedLocales)[number] | null {
  return value === "fr" || value === "ar" ? value : null;
}

// Set by middleware for /fr and /ar URLs. Only app/global-not-found.tsx reads it,
// so unknown localized URLs get a localized 404 while pages stay prerendered.
export const notFoundLocaleHeader = "x-ta3na-locale";

export function requestLocale(value: string | null | undefined): Locale {
  return value === "fr" || value === "ar" ? value : defaultLocale;
}

export const localizedPaths = ["/", "/about", "/faq", "/support"] as const;
export type LocalizedPath = (typeof localizedPaths)[number];

export const policyPaths = [
  "/community-standards",
  "/privacy",
  "/terms",
  "/account-deletion",
] as const;

export const indexablePaths = [...localizedPaths, ...policyPaths] as const;

export function localeSpec(locale: Locale) {
  return localeSpecs.find((spec) => spec.code === locale) ?? localeSpecs[0];
}

export function localePath(locale: Locale, path: LocalizedPath): string {
  if (locale === defaultLocale) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

export function localeFromPathname(pathname: string | null | undefined): Locale {
  const segment = (pathname ?? "/").split("/").filter(Boolean)[0];
  return segment === "fr" || segment === "ar" ? segment : defaultLocale;
}

export function unlocalizedPath(pathname: string | null | undefined): string {
  const normalizedPath = pathname ?? "/";
  const locale = localeFromPathname(normalizedPath);
  if (locale === defaultLocale) return normalizedPath || "/";
  const path = normalizedPath.slice(locale.length + 1);
  return path || "/";
}

export function isLocalizedPath(pathname: string): pathname is LocalizedPath {
  return (localizedPaths as readonly string[]).includes(pathname);
}

export function languageAlternates(path: LocalizedPath) {
  return {
    ar: localePath("ar", path),
    en: localePath("en", path),
    fr: localePath("fr", path),
    "x-default": localePath("en", path),
  };
}
